import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

const MAX_DIMENSION = 1920
const QUALITY = 0.8

export default class extends Controller {
  static targets = ["input", "status", "statusText", "progressBar", "progressFill", "error"]
  static values = { accept: String }

  connect() {
    // Ensure the input accepts camera capture on mobile when image/* is specified
    if (this.acceptValue.includes("image/")) {
      this.inputTarget.setAttribute("capture", "environment")
    }
  }

  async handleFiles(event) {
    const files = Array.from(event.target.files)
    if (!files.length) return

    this.#clearError()
    this.#showStatus()

    for (const file of files) {
      try {
        const uploadFile = file.type.startsWith("image/")
          ? await this.#compressImage(file)
          : file

        await this.#directUpload(uploadFile)
      } catch (err) {
        this.#showError(err.message || "Upload failed. Please try again.")
        return
      }
    }

    this.#setStatusText(`${files.length} file${files.length > 1 ? "s" : ""} ready`)
    this.#hideProgressBar()
  }

  // ---------------------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------------------

  async #compressImage(file) {
    return new Promise((resolve, reject) => {
      const img = new Image()
      const url = URL.createObjectURL(file)

      img.onload = () => {
        URL.revokeObjectURL(url)

        let { width, height } = img

        if (width <= MAX_DIMENSION && height <= MAX_DIMENSION) {
          resolve(file)
          return
        }

        const scale = MAX_DIMENSION / Math.max(width, height)
        width = Math.round(width * scale)
        height = Math.round(height * scale)

        const canvas = document.createElement("canvas")
        canvas.width = width
        canvas.height = height

        const ctx = canvas.getContext("2d")
        ctx.drawImage(img, 0, 0, width, height)

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error("Image compression failed"))
              return
            }
            resolve(new File([blob], file.name, { type: "image/jpeg" }))
          },
          "image/jpeg",
          QUALITY
        )
      }

      img.onerror = () => {
        URL.revokeObjectURL(url)
        reject(new Error("Could not read image"))
      }

      img.src = url
    })
  }

  #directUpload(file) {
    return new Promise((resolve, reject) => {
      const url = this.inputTarget.dataset.directUploadUrl
      const upload = new DirectUpload(file, url, {
        directUploadWillStoreFileWithXHR: (xhr) => {
          xhr.upload.addEventListener("progress", (event) => {
            if (event.lengthComputable) {
              const pct = Math.round((event.loaded / event.total) * 100)
              this.#setProgress(pct)
            }
          })
        }
      })

      this.#showProgressBar()
      this.#setStatusText(`Uploading ${file.name}…`)

      upload.create((error, blob) => {
        if (error) {
          reject(error)
        } else {
          // Append a hidden input so Rails picks up the signed_id
          const hidden = document.createElement("input")
          hidden.type = "hidden"
          hidden.name = this.inputTarget.name
          hidden.value = blob.signed_id
          this.element.appendChild(hidden)
          resolve(blob)
        }
      })
    })
  }

  #showStatus() {
    if (this.hasStatusTarget) {
      this.statusTarget.classList.remove("hidden")
    }
  }

  #setStatusText(text) {
    if (this.hasStatusTextTarget) {
      this.statusTextTarget.textContent = text
    }
  }

  #showProgressBar() {
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.classList.remove("hidden")
    }
  }

  #hideProgressBar() {
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.classList.add("hidden")
    }
  }

  #setProgress(pct) {
    if (this.hasProgressFillTarget) {
      this.progressFillTarget.style.width = `${pct}%`
    }
  }

  #showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message
      this.errorTarget.classList.remove("hidden")
    }
    this.#hideProgressBar()
    if (this.hasStatusTarget) {
      this.statusTarget.classList.add("hidden")
    }
  }

  #clearError() {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = ""
      this.errorTarget.classList.add("hidden")
    }
  }
}
